// frontend/src/components/Individual/StealthAgent.jsx
// Runs as an independent popup/desktop window — invisible to screen share
// Has its own socket + microphone. Auto-exits after 2 hours.
import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { loadFingerprint, checkSpeaker, FFT_SIZE } from '../../utils/speakerDetector';
import { useAudioDevices } from '../../hooks/useAudioDevices';
import AudioDevicePicker from '../Shared/AudioDevicePicker';
import AiContextModal from '../Shared/AiContextModal';
import { buildAudioPipeline, captureMic, captureSystemAudio } from '../../hooks/useAudioCapture';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DEFAULT_DURATION_MS = 2 * 60 * 60 * 1000;

const StealthAgent = () => {
  const params   = new URLSearchParams(window.location.search);
  const sessionKey = params.get('sk') || '';
  const roomKey    = params.get('rk') || '';

  const [connected, setConnected]   = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [aiMode, setAiMode]         = useState('coach');
  const [aiResponses, setAiResponses] = useState([]);
  const [aiThinking, setAiThinking] = useState(false);
  const [liveText, setLiveText]     = useState('');
  const [error, setError]           = useState('');
  const [sessionDurationMs, setSessionDurationMs] = useState(DEFAULT_DURATION_MS);
  const [timeLeft, setTimeLeft]     = useState(DEFAULT_DURATION_MS);
  const [micError, setMicError]     = useState('');

  const sockRef          = useRef(null);
  const isRecRef         = useRef(false);
  const audioCtxRef      = useRef(null);
  const processorRef     = useRef(null);
  const streamRef        = useRef(null);
  const analyserRef      = useRef(null);
  const detectionRef     = useRef(null);
  const fingerprintRef   = useRef(loadFingerprint());
  const speakerIsUserRef = useRef(false);
  const aiEndRef         = useRef(null);
  const startTime        = useRef(Date.now());
  const channelRef       = useRef(null);

  const [speakerLabel, setSpeakerLabel] = useState(
    fingerprintRef.current ? 'Detecting...' : 'No voice trained'
  );
  const [similarity, setSimilarity]   = useState(null);

  const [showContextModal, setShowContextModal] = useState(false);
  const [aiRoleLabel, setAiRoleLabel]           = useState('');
  const [audioSource, setAudioSource]           = useState('mic'); // 'mic' | 'system'
  const pipelineStopRef                         = useRef(null);
  const [hidden, setHidden]                     = useState(false); // quick-hide mode
  const [showSetupGuide, setShowSetupGuide]     = useState(false);

  const { devices, deviceId, selectDevice, getAudioConstraints, refreshAfterPermission } = useAudioDevices();

  // Room chat state
  const [activeTab, setActiveTab]     = useState('ai'); // 'ai' | 'chat'
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput]     = useState('');
  const [chatUnread, setChatUnread]   = useState(0);
  const chatEndRef                    = useRef(null);
  const myUserIdRef                   = useRef(null);

  // ── Fetch session duration from admin settings ────────────────────────────
  useEffect(() => {
    fetch(`${API_URL}/settings/session`)
      .then(r => r.json())
      .then(data => {
        if (data.sessionDurationHours) {
          const ms = data.sessionDurationHours * 60 * 60 * 1000;
          setSessionDurationMs(ms);
          setTimeLeft(ms);
        }
      })
      .catch(() => {}); // fallback to default if unreachable
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll AI responses ──────────────────────────────────────────────
  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiResponses, aiThinking]);

  // ── Countdown + auto-close (uses admin-configured session duration) ────────
  useEffect(() => {
    if (!sessionDurationMs) return;
    startTime.current = Date.now(); // reset when duration is known
    const tick = setInterval(() => {
      const remaining = Math.max(0, sessionDurationMs - (Date.now() - startTime.current));
      setTimeLeft(remaining);
      if (remaining === 0) {
        clearInterval(tick);
        sockRef.current?.disconnect();
        setTimeout(() => window.close(), 3000);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [sessionDurationMs]);

  // ── Camouflage: set window title to something inconspicuous ─────────────────
  useEffect(() => {
    document.title = 'System Preferences';
    return () => { document.title = 'VoiceApp'; };
  }, []);

  // ── Quick-hide: press H to toggle blank screen (hides from view instantly) ──
  useEffect(() => {
    const handler = (e) => {
      // H key — toggle hidden (blank) mode
      if (e.key === 'h' || e.key === 'H') {
        if (!e.target.matches('input,textarea')) {
          setHidden(prev => !prev);
        }
      }
      // Escape — restore if hidden
      if (e.key === 'Escape') setHidden(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── BroadcastChannel — receive mode changes from main window ──────────────
  useEffect(() => {
    const ch = new BroadcastChannel('stealth-ai');
    channelRef.current = ch;
    ch.onmessage = (e) => {
      if (e.data.type === 'mode-change') {
        setAiMode(e.data.mode);
        sockRef.current?.emit('set-ai-mode', {
          aiEnabled: true, stealthMode: true, mode: e.data.mode
        });
        setAiResponses([]);
      }
    };
    return () => ch.close();
  }, []);

  // ── Socket setup ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!sessionKey || !roomKey) {
      setError('Missing session or room key. Launch from the Create Room page.');
      return;
    }

    const sock = io(SOCKET_URL, { autoConnect: false, transports: ['websocket', 'polling'] });
    sockRef.current = sock;

    sock.on('connect', () => {
      setConnected(true);
      setError('');
      sock.emit('join-room', { roomKey, sessionKey });
      sock.emit('set-ai-mode', { aiEnabled: true, stealthMode: true, mode: aiMode });
    });

    sock.on('room-joined', (data) => {
      if (data.userId) myUserIdRef.current = data.userId;
    });

    sock.on('message', (data) => {
      const isSelf = data.userId === myUserIdRef.current || data.self;
      setChatMessages(prev => [...prev, {
        role: isSelf ? 'user' : 'other',
        text: data.message,
        userId: data.userId,
        userName: data.userName || `User ${String(data.userId || '').slice(-4)}`,
        timestamp: data.timestamp || new Date().toISOString()
      }]);
      // Badge unread count when chat tab is not active
      setChatUnread(n => n + (isSelf ? 0 : 1));
    });

    sock.on('disconnect', () => {
      setConnected(false);
      isRecRef.current = false;
      setIsRecording(false);
    });

    sock.on('transcript', (data) => {
      if (data.isFinal) setLiveText('');
      else setLiveText(data.text || '');
    });

    sock.on('ai-thinking', (data) => setAiThinking(data.thinking));

    sock.on('ai-response', (data) => {
      setAiThinking(false);
      setAiResponses(prev => [...prev, {
        text: data.text,
        mode: data.mode || aiMode,
        timestamp: data.timestamp || new Date().toISOString()
      }]);
      // Notify main window
      channelRef.current?.postMessage({ type: 'ai-response', text: data.text, mode: data.mode });
    });

    sock.on('ai-error', (data) => {
      setAiThinking(false);
      setError(data.message);
    });

    sock.on('error', (data) => setError(data.message));

    sock.connect();
    return () => sock.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Voice recording (supports mic + system/tab audio) ────────────────────
  const startRecording = async () => {
    try {
      setMicError('');
      isRecRef.current = true;

      let stream;
      if (audioSource === 'system') {
        stream = await captureSystemAudio();
        // Auto-stop when user closes the share
        stream.getAudioTracks()[0].onended = () => {
          if (isRecRef.current) stopRecording();
        };
      } else {
        const constraints = getAudioConstraints();
        stream = await captureMic(constraints);
        refreshAfterPermission();
      }

      streamRef.current = stream;

      // For mic mode — wire up analyser for speaker detection
      if (audioSource === 'mic') {
        const nativeSr = stream.getAudioTracks()[0]?.getSettings()?.sampleRate || 48000;
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: nativeSr });
        const src = audioCtxRef.current.createMediaStreamSource(stream);

        analyserRef.current = audioCtxRef.current.createAnalyser();
        analyserRef.current.fftSize = FFT_SIZE;
        analyserRef.current.smoothingTimeConstant = 0.4;
        src.connect(analyserRef.current);

        // Speaker detection every 500ms
        detectionRef.current = setInterval(() => {
          if (!isRecRef.current || !analyserRef.current) return;
          const fp = fingerprintRef.current;
          if (!fp) { setSpeakerLabel('No voice trained'); return; }
          const result = checkSpeaker(analyserRef.current, fp);
          if (!result) return;
          setSimilarity(Math.round(result.similarity * 100));
          const isUser = result.isUser;
          if (isUser !== speakerIsUserRef.current) {
            speakerIsUserRef.current = isUser;
            sockRef.current?.emit('set-speaker', { isUser });
          }
          setSpeakerLabel(isUser ? 'You' : 'Interviewer');
        }, 500);

        audioCtxRef.current.close();   // will be recreated by buildAudioPipeline
        audioCtxRef.current = null;
        analyserRef.current = null;    // will be invalid after close; keep detection timer but guard
      } else {
        // System audio — speaker detection N/A
        setSpeakerLabel('Tab Audio');
        setSimilarity(null);
      }

      // Shared PCM pipeline for both modes
      const pipeline = buildAudioPipeline(stream, (pcmBuf) => {
        if (isRecRef.current) sockRef.current?.emit('audio-data', pcmBuf);
      });
      pipelineStopRef.current = pipeline.stop;
      // Keep refs for stopRecording cleanup
      audioCtxRef.current = pipeline.audioCtx;
      processorRef.current = pipeline.processor;

      // Re-attach analyser for mic mode using the new audioCtx
      if (audioSource === 'mic') {
        clearInterval(detectionRef.current);
        const src2 = pipeline.audioCtx.createMediaStreamSource(stream);
        analyserRef.current = pipeline.audioCtx.createAnalyser();
        analyserRef.current.fftSize = FFT_SIZE;
        analyserRef.current.smoothingTimeConstant = 0.4;
        src2.connect(analyserRef.current);

        detectionRef.current = setInterval(() => {
          if (!isRecRef.current || !analyserRef.current) return;
          const fp = fingerprintRef.current;
          if (!fp) { setSpeakerLabel('No voice trained'); return; }
          const result = checkSpeaker(analyserRef.current, fp);
          if (!result) return;
          setSimilarity(Math.round(result.similarity * 100));
          const isUser = result.isUser;
          if (isUser !== speakerIsUserRef.current) {
            speakerIsUserRef.current = isUser;
            sockRef.current?.emit('set-speaker', { isUser });
          }
          setSpeakerLabel(isUser ? 'You' : 'Interviewer');
        }, 500);
      }

      sockRef.current?.emit('start-transcription', { roomKey, sessionKey });
      setIsRecording(true);
    } catch (err) {
      isRecRef.current = false;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setMicError(audioSource === 'system'
          ? 'Sharing cancelled. Click REC and select a tab, then tick "Share tab audio".'
          : 'Microphone access denied. Please grant permission.');
      } else if (err.message?.includes('No audio track')) {
        setMicError('No audio captured — make sure "Share tab audio" is checked in the Chrome picker.');
      } else if (err.name === 'OverconstrainedError') {
        setMicError('Selected device unavailable. Choose a different input.');
      } else {
        setMicError(`Audio error: ${err.message}`);
      }
    }
  };

  const stopRecording = () => {
    isRecRef.current = false;
    clearInterval(detectionRef.current);
    if (pipelineStopRef.current) { pipelineStopRef.current(); pipelineStopRef.current = null; }
    processorRef.current = null;
    analyserRef.current  = null;
    audioCtxRef.current  = null;
    streamRef.current    = null;
    sockRef.current?.emit('stop-transcription', { roomKey });
    setIsRecording(false);
    setLiveText('');
    setSpeakerLabel(fingerprintRef.current ? 'Detecting...' : 'No voice trained');
    setSimilarity(null);
  };

  // ── Room Chat ─────────────────────────────────────────────────────────────
  const sendChat = (e) => {
    e.preventDefault();
    const text = chatInput.trim();
    if (!text || !connected) return;
    // Add locally (server broadcasts to others, not back to sender)
    setChatMessages(prev => [...prev, { role: 'user', userName: 'You', text, timestamp: new Date().toISOString() }]);
    sockRef.current?.emit('send-message', { roomKey, message: text });
    setChatInput('');
  };

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const switchMode = (mode) => {
    setAiMode(mode);
    setAiResponses([]);
    sockRef.current?.emit('set-ai-mode', { aiEnabled: true, stealthMode: true, mode });
    channelRef.current?.postMessage({ type: 'mode-change', mode });
  };

  const handleContextConfirm = (systemPrompt, roleLabel) => {
    setShowContextModal(false);
    setAiRoleLabel(roleLabel);
    setAiResponses([]);
    // Send system prompt to backend
    if (systemPrompt) {
      sockRef.current?.emit('switch-agent', { agent: roleLabel, systemPrompt });
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const fmt = (ms) => {
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  };

  const urgent = timeLeft < 5 * 60 * 1000; // last 5 min

  // ── Render ────────────────────────────────────────────────────────────────
  if (!sessionKey || !roomKey) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <p className="text-red-400 text-sm text-center">
          Missing session / room key.<br/>Launch from the Create Room page.
        </p>
      </div>
    );
  }

  // ── Hidden / blank mode (press H to toggle) ──────────────────────────────
  if (hidden) {
    return (
      <div
        className="min-h-screen bg-gray-950 flex items-center justify-center cursor-pointer"
        onClick={() => setHidden(false)}
        title="Click or press H to restore"
      >
        {/* Blank screen — looks like an inactive black window */}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col p-3 text-sm select-none overflow-hidden">
      {showContextModal && (
        <AiContextModal
          dark
          onConfirm={handleContextConfirm}
          onCancel={() => setShowContextModal(false)}
        />
      )}

      {/* ── Zoom / Screen Share Setup Guide ─────────────────────────────── */}
      {showSetupGuide && (
        <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-5 max-w-sm w-full text-xs">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-sm">🕵️ Keep Stealth Hidden from Zoom</h3>
              <button onClick={() => setShowSetupGuide(false)} className="text-gray-500 hover:text-gray-300">✕</button>
            </div>

            <div className="space-y-4 text-gray-300">
              {/* Option 1 — Best */}
              <div className="bg-green-950 border border-green-800 rounded-xl p-3">
                <p className="text-green-400 font-semibold mb-1.5">✅ Option 1 — Best (Share a Window, not Screen)</p>
                <ol className="list-decimal list-inside space-y-1 text-green-300">
                  <li>In Zoom → click <strong>Share Screen</strong></li>
                  <li>Choose the <strong>"Window"</strong> tab (not "Screen")</li>
                  <li>Select your <strong>main browser window</strong></li>
                  <li>Click <strong>Share</strong></li>
                </ol>
                <p className="text-green-500 mt-2">This popup will NOT appear — Zoom only captures the selected window.</p>
              </div>

              {/* Option 2 — Second monitor */}
              <div className="bg-blue-950 border border-blue-800 rounded-xl p-3">
                <p className="text-blue-400 font-semibold mb-1.5">🖥️ Option 2 — Two Monitors</p>
                <p className="text-blue-300">Move this window to your <strong>second monitor</strong>. Share only your primary screen in Zoom. This window stays private.</p>
              </div>

              {/* Option 3 — Quick hide */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-3">
                <p className="text-gray-300 font-semibold mb-1.5">⌨️ Option 3 — Quick Hide Shortcut</p>
                <p className="text-gray-400">Press <kbd className="bg-gray-700 text-gray-200 px-1.5 py-0.5 rounded font-mono">H</kbd> at any time to instantly blank this window. Press <kbd className="bg-gray-700 text-gray-200 px-1.5 py-0.5 rounded font-mono">H</kbd> or click to restore.</p>
              </div>

              {/* Option 4 — Different browser */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-3">
                <p className="text-gray-300 font-semibold mb-1.5">🦊 Option 4 — Different Browser</p>
                <p className="text-gray-400">Open the interview in Chrome, open this stealth link in Firefox (or Edge). Zoom sharing one browser won't capture the other.</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(window.location.href); }}
                  className="mt-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-xs font-medium"
                >
                  📋 Copy this URL
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowSetupGuide(false)}
              className="mt-4 w-full py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-xs font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between mb-2 shrink-0">
        <div className="flex items-center space-x-2">
          <span className={`h-2 w-2 rounded-full ${connected ? 'bg-green-400 animate-pulse' : 'bg-red-500'}`} />
          <span className="text-gray-500 text-xs font-mono tracking-widest">STEALTH</span>
          {/* Shield button — opens screen share setup guide */}
          <button
            onClick={() => setShowSetupGuide(true)}
            title="How to hide this from Zoom / screen share"
            className="text-gray-700 hover:text-yellow-400 transition-colors"
          >
            🛡️
          </button>
          {/* Quick-hide button */}
          <button
            onClick={() => setHidden(true)}
            title="Blank screen now — press H or click to restore"
            className="text-gray-700 hover:text-gray-400 transition-colors text-xs font-mono"
          >
            [H]ide
          </button>
        </div>

        {/* Speaker indicator */}
        {isRecording && fingerprintRef.current && (
          <div className={`flex items-center space-x-1.5 px-2 py-0.5 rounded text-xs font-mono ${
            speakerLabel === 'Interviewer'
              ? 'bg-green-900 text-green-300 border border-green-700'
              : speakerLabel === 'You'
                ? 'bg-gray-800 text-gray-400 border border-gray-700'
                : 'bg-gray-900 text-gray-600'
          }`}>
            <span className={`h-1.5 w-1.5 rounded-full ${
              speakerLabel === 'Interviewer' ? 'bg-green-400 animate-pulse' : 'bg-gray-600'
            }`} />
            <span>{speakerLabel}</span>
            {similarity !== null && (
              <span className="opacity-50">{similarity}%</span>
            )}
          </div>
        )}

        <div className="flex items-center space-x-2">
          {/* Mode tabs */}
          <div className="flex rounded overflow-hidden border border-gray-800">
            {['coach','answer'].map(m => (
              <button key={m}
                onClick={() => switchMode(m)}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  aiMode === m
                    ? m === 'coach' ? 'bg-blue-700 text-white' : 'bg-green-700 text-white'
                    : 'bg-gray-900 text-gray-500 hover:text-gray-300'
                }`}
              >
                {m === 'coach' ? 'Coach' : 'Answer'}
              </button>
            ))}
          </div>

          {/* Role / context button */}
          <button
            onClick={() => setShowContextModal(true)}
            title={aiRoleLabel ? `Role: ${aiRoleLabel} — click to change` : 'Set AI context / role'}
            className={`px-2 py-1 rounded text-xs border transition-colors ${
              aiRoleLabel
                ? 'border-purple-600 bg-purple-950 text-purple-300'
                : 'border-gray-700 bg-gray-900 text-gray-500 hover:text-gray-300'
            }`}
          >
            {aiRoleLabel ? `✦ ${aiRoleLabel}` : '✦ Set Role'}
          </button>

          {/* Countdown */}
          <span className={`text-xs font-mono tabular-nums ${urgent ? 'text-red-400 animate-pulse' : 'text-gray-600'}`}>
            {fmt(timeLeft)}
          </span>
        </div>
      </div>

      {/* ── Live text ── */}
      {liveText && (
        <div className="mb-2 px-3 py-1.5 bg-gray-900 rounded border border-gray-800 shrink-0">
          <p className="text-blue-400 text-xs italic truncate">{liveText}</p>
        </div>
      )}

      {/* ── Tabs ── */}
      <div className="flex shrink-0 border-b border-gray-800 mb-2">
        {[['ai','AI'], ['chat','Room Chat']].map(([tab, label]) => (
          <button key={tab}
            onClick={() => { setActiveTab(tab); if (tab === 'chat') setChatUnread(0); }}
            className={`px-4 py-1.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-600 hover:text-gray-400'
            }`}>
            {label}
            {tab === 'chat' && chatUnread > 0 && (
              <span className="ml-1 bg-red-600 text-white rounded-full px-1.5 text-xs leading-4 inline-block">{chatUnread}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── AI Tab ── */}
      {activeTab === 'ai' && (
        <div className="flex-1 overflow-y-auto space-y-2 pr-0.5 min-h-0">
          {aiResponses.length === 0 && !aiThinking && !error && (
            <div className="flex flex-col items-center justify-center h-full space-y-1 opacity-40">
              <svg className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
              </svg>
              <p className="text-gray-600 text-xs text-center">
                {aiMode === 'coach' ? 'Speak — coaching tips appear here' : 'Speak — full answers appear here'}
              </p>
            </div>
          )}

          {aiResponses.map((r, i) => (
            <div key={i} className={`rounded-lg p-2.5 border-l-2 ${
              r.mode === 'coach' ? 'bg-gray-900 border-blue-600' : 'bg-gray-900 border-green-600'
            }`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-bold uppercase tracking-wide ${
                  r.mode === 'coach' ? 'text-blue-400' : 'text-green-400'
                }`}>{r.mode === 'coach' ? 'Coach' : 'Answer'}</span>
                <span className="text-gray-700 text-xs">{new Date(r.timestamp).toLocaleTimeString()}</span>
              </div>
              <p className="text-gray-100 text-xs leading-relaxed">{r.text}</p>
            </div>
          ))}

          {aiThinking && (
            <div className="rounded-lg p-2.5 bg-gray-900 border-l-2 border-purple-700">
              <div className="flex items-center space-x-2">
                {[0,150,300].map(d => (
                  <div key={d} className="h-1.5 w-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                ))}
                <span className="text-gray-600 text-xs">thinking...</span>
              </div>
            </div>
          )}

          {(error || micError) && (
            <div className="rounded-lg p-2.5 bg-red-950 border-l-2 border-red-700">
              <p className="text-red-400 text-xs">{error || micError}</p>
            </div>
          )}

          {timeLeft === 0 && (
            <div className="rounded-lg p-2.5 bg-yellow-950 border border-yellow-800 text-center">
              <p className="text-yellow-400 text-xs">Session expired — closing in 3s...</p>
            </div>
          )}

          <div ref={aiEndRef} />
        </div>
      )}

      {/* ── Chat Tab ── */}
      {activeTab === 'chat' && (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto space-y-2 pr-0.5 min-h-0">
            {chatMessages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full space-y-1 opacity-40">
                <svg className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <p className="text-gray-600 text-xs text-center">Room chat — visible to all participants</p>
              </div>
            )}

            {chatMessages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 ${
                  m.role === 'user'
                    ? 'bg-blue-700 text-white'
                    : 'bg-gray-800 border border-gray-700 text-gray-100'
                }`}>
                  {m.role === 'other' && (
                    <span className="text-xs text-gray-400 font-medium block mb-0.5">{m.userName}</span>
                  )}
                  <p className="text-xs leading-relaxed">{m.text}</p>
                  <p className="text-xs opacity-40 mt-0.5 text-right">{new Date(m.timestamp).toLocaleTimeString()}</p>
                </div>
              </div>
            ))}

            <div ref={chatEndRef} />
          </div>

          {/* Chat input */}
          <form onSubmit={sendChat} className="mt-2 flex space-x-1.5 shrink-0">
            <input
              type="text"
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              placeholder={connected ? 'Message the room...' : 'Connecting...'}
              disabled={!connected}
              className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-xs text-gray-100 placeholder-gray-700 focus:outline-none focus:border-blue-600 disabled:opacity-50"
            />
            <button type="submit" disabled={!connected || !chatInput.trim()}
              className="px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-xs font-medium transition-colors">
              Send
            </button>
          </form>
        </div>
      )}

      {/* ── Bottom controls (AI tab only) ── */}
      {activeTab === 'ai' && (
        <div className="mt-2 shrink-0 space-y-1.5">
          {aiResponses.length > 0 && (
            <button onClick={() => setAiResponses([])}
              className="w-full text-xs text-gray-800 hover:text-gray-600 py-0.5">
              clear
            </button>
          )}

          {/* Source + device row (only when not recording) */}
          {!isRecording && (
            <div className="flex items-center space-x-1.5">
              {/* Mic / Tab toggle */}
              <div className="flex rounded overflow-hidden border border-gray-700 flex-shrink-0">
                {[['mic','🎤','Mic'],['system','🖥️','Tab']].map(([id, icon, lbl]) => (
                  <button key={id} type="button"
                    onClick={() => setAudioSource(id)}
                    className={`px-2 py-1 text-xs font-medium transition-colors ${
                      audioSource === id
                        ? id === 'system' ? 'bg-blue-700 text-white' : 'bg-gray-600 text-white'
                        : 'bg-gray-900 text-gray-500 hover:text-gray-300'
                    }`}>
                    {icon} {lbl}
                  </button>
                ))}
              </div>

              {/* Mic device picker */}
              {audioSource === 'mic' && (
                <div className="flex-1 min-w-0">
                  <AudioDevicePicker
                    devices={devices}
                    deviceId={deviceId}
                    onSelect={selectDevice}
                    disabled={isRecording}
                    compact
                    dark
                  />
                </div>
              )}

              {/* Tab audio hint */}
              {audioSource === 'system' && (
                <span className="text-xs text-blue-400 truncate leading-tight">
                  Pick tab → tick "Share tab audio"
                </span>
              )}
            </div>
          )}

          {/* Record / Stop */}
          <button
            onClick={isRecording ? stopRecording : startRecording}
            disabled={!connected}
            className={`w-full py-2 rounded-lg text-xs font-bold transition-all ${
              isRecording
                ? 'bg-red-700 hover:bg-red-600 text-white animate-pulse'
                : connected
                  ? audioSource === 'system'
                    ? 'bg-blue-800 hover:bg-blue-700 text-blue-100 border border-blue-600'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-200 border border-gray-700'
                  : 'bg-gray-900 text-gray-700 cursor-not-allowed border border-gray-800'
            }`}
          >
            {isRecording
              ? '⏹ STOP'
              : connected
                ? audioSource === 'system' ? '🖥️ SHARE TAB & REC' : '🎙 START RECORDING'
                : 'Connecting...'}
          </button>
        </div>
      )}
    </div>
  );
};

export default StealthAgent;
